/**
 * FIN-12216 — "empty-baseline" false-fire guard for linear-regression thresholds.
 *
 * A linear-regression comparison in the formula fires when
 *     observed  <op>  (coef * baseline + bias)
 * where `baseline` is the LR `val` field (e.g. a 120-day count). When `bias === 0` and the
 * baseline is 0 — an entity with no history — the threshold collapses to 0, so the rule fires
 * on ANY value (counts are >= 0). The only thing that prevents this is a sibling condition,
 * ANDed in the same group, that asserts the baseline is > 0.
 *
 * These helpers inspect the RAQB `functionAggregation` tree and surface a config-time warning
 * for exactly that degenerate case (bias === 0 with no baseline > 0 guard), so the rule author
 * is nudged to add the guard. Pure functions — unit-tested independently of the React tab.
 */

const LINEAR_REGRESSION_FUNC = 'number.LINEAR_REGRESSION';

const isRule = (node) => !!node && node.type === 'rule';
const isGroup = (node) =>
  !!node && (node.type === 'group' || node.type === 'rule_group');

// children1 can be an array ([{...}]) or an id-keyed object map ({id: {...}}).
const childrenAsArray = (group) => {
  const children = group && (group.children1 || group.children);
  if (Array.isArray(children)) return children;
  if (children && typeof children === 'object') return Object.values(children);
  return [];
};

// Returns the LINEAR_REGRESSION args ({coef, val, bias}) if this rule's RHS is one, else null.
const linearRegressionArgs = (rule) => {
  const props = rule && rule.properties;
  if (
    !props ||
    !Array.isArray(props.valueSrc) ||
    props.valueSrc[0] !== 'func'
  ) {
    return null;
  }
  const rhs = Array.isArray(props.value) ? props.value[0] : null;
  if (!rhs || rhs.func !== LINEAR_REGRESSION_FUNC) return null;
  return rhs.args || {};
};

// True when `props` is a positive lower-bound guard on `field`:
// field > 0 (or higher), field >= 1 (or higher), or field != 0.
const isBaselineGuard = (props, field) => {
  if (!props || props.field !== field) return false;
  const raw = Array.isArray(props.value) ? props.value[0] : undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return false;
  switch (props.operator) {
    case 'greater':
      return n >= 0; // field > 0 (n === 0) or a higher floor
    case 'greater_or_equal':
      return n >= 1; // field >= 1
    case 'not_equal':
      return n === 0; // field != 0
    default:
      return false;
  }
};

/**
 * Walk the functionAggregation tree and collect every linear-regression comparison that fires on
 * an empty baseline: bias === 0 with no sibling `baseline > 0` guard in the same AND group.
 * @returns {Array<{field: string|null, baselineField: string|null}>}
 */
export const findEmptyBaselineRiskRules = (tree) => {
  const risks = [];

  const walk = (group) => {
    if (!isGroup(group)) return;
    const children = childrenAsArray(group);
    const conjunction = (
      (group.properties && group.properties.conjunction) ||
      'AND'
    ).toUpperCase();
    const negated = !!(group.properties && group.properties.not);
    // A sibling guard only protects the LR rule when the group is a plain AND (not negated).
    const guardable = conjunction === 'AND' && !negated;

    children.forEach((child) => {
      if (isGroup(child)) {
        walk(child);
        return;
      }
      if (!isRule(child)) return;

      const args = linearRegressionArgs(child);
      if (!args) return;

      const bias =
        args.bias && typeof args.bias.value !== 'undefined'
          ? Number(args.bias.value)
          : NaN;
      // Only the degenerate bias === 0 case fires on any value. bias > 0 still requires the
      // observed value to clear the bias, which can be legitimate — don't warn there.
      if (bias !== 0) return;

      const baselineField =
        args.val && args.val.valueSrc === 'field' ? args.val.value : null;

      const guarded =
        guardable &&
        !!baselineField &&
        children.some(
          (sibling) =>
            sibling !== child &&
            isRule(sibling) &&
            isBaselineGuard(sibling.properties, baselineField)
        );

      if (!guarded) {
        risks.push({
          field: (child.properties && child.properties.field) || null,
          baselineField: baselineField || null
        });
      }
    });
  };

  walk(tree);
  return risks;
};

/** Build the antd Alert message for the risks, or null when there is nothing to warn about. */
export const emptyBaselineWarningMessage = (risks) => {
  if (!risks || risks.length === 0) return null;
  const fields = risks.map((r) => r.field).filter(Boolean);
  const subject = fields.length
    ? `Rule${fields.length > 1 ? 's' : ''} on ${fields.join(', ')}`
    : 'This rule';
  const example = risks.find((r) => r.baselineField) || risks[0];
  const guardHint = example.baselineField
    ? `Add a "${example.baselineField} > 0" condition (AND) to require history before it can fire.`
    : 'Add a baseline "> 0" condition (AND) to require history before it can fire.';
  return (
    `${subject} use a linear-regression threshold with bias = 0. When the baseline is 0 ` +
    `(an entity with no history) the threshold is 0, so it fires on any value. ${guardHint}`
  );
};
